<template>
  <div class="homepage-editor" style="min-height: 100vh; background: #f5f5f5;">
    <!-- 상단 툴바 영역 (sticky) -->
    <div
      class="editor-toolbar-container"
      style="
        position: sticky;
        top: 0;
        z-index: 10;
        background: #fff;
        border-bottom: 1px solid #f0f0f0;
        padding: 12px 24px;
      "
    >
      <div style="display: flex; align-items: center; justify-content: space-between;">
        <!-- 좌측: 페이지 선택 -->
        <page-selector />
        <!-- 우측: 저장/미리보기 툴바 -->
        <editor-toolbar />
      </div>
    </div>

    <!-- 에디터 본문 영역 -->
    <div class="editor-content" style="padding: 24px; overflow-y: auto;">
      <!-- 데이터 로딩 중 -->
      <div v-if="isLoading" style="text-align: center; padding: 60px 0;">
        <a-spin size="large" tip="데이터를 불러오는 중..." />
      </div>

      <!-- 데이터 로드 실패 또는 미로드 -->
      <a-empty
        v-else-if="!currentPageData"
        description="데이터를 불러오는 중..."
      />

      <!-- 메인 페이지 WYSIWYG 프리뷰 -->
      <main-page-editor v-else-if="currentPage === 'main'" />

      <!-- 가격안내 페이지 에디터 -->
      <pricing-page-editor v-else-if="currentPage === 'pricing'" />

      <!-- 서비스소개 페이지 에디터 -->
      <service-page-editor v-else-if="currentPage === 'services'" />

      <!-- 다운로드 페이지 에디터 -->
      <download-page-editor v-else-if="currentPage === 'downloads'" />
    </div>

    <!-- 편집 Drawer/Modal 호스트 (activeEditor 상태 기반 렌더링) -->
    <editor-drawer-host />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import PageSelector from './editor/PageSelector.vue';
import EditorToolbar from './editor/EditorToolbar.vue';
import MainPageEditor from './editor/MainPageEditor.vue';
import EditorDrawerHost from './editor/EditorDrawerHost.vue';
import PricingPageEditor from './editor/PricingPageEditor.vue';
import ServicePageEditor from './editor/ServicePageEditor.vue';
import DownloadPageEditor from './editor/DownloadPageEditor.vue';

export default {
  name: 'HomepageEditor',

  components: {
    PageSelector,
    EditorToolbar,
    MainPageEditor,
    EditorDrawerHost,
    PricingPageEditor,
    ServicePageEditor,
    DownloadPageEditor,
  },

  data() {
    return {
      // 초기 데이터 로딩 상태
      isLoading: false,
    };
  },

  computed: {
    ...mapGetters('homepageEditor', ['isDirty', 'currentPageData']),
    currentPage() {
      return this.$store.state.homepageEditor.currentPage;
    },
  },

  // 라우트 이탈 전 dirty 상태 확인
  beforeRouteLeave(to, from, next) {
    if (this.isDirty) {
      const answer = window.confirm(
        '저장되지 않은 변경사항이 있습니다. 페이지를 벗어나면 변경사항이 사라집니다. 계속하시겠습니까?'
      );
      if (answer) {
        next();
      } else {
        next(false);
      }
    } else {
      next();
    }
  },

  watch: {
    // 페이지 전환 시 해당 페이지 데이터 자동 로드
    currentPage(newPage) {
      this.loadPageData(newPage);
    },
  },

  async mounted() {
    // 마운트 시 기본 페이지(main) 데이터 로드
    this.isLoading = true;
    try {
      await this.loadPageData('main');
      // 로드 완료 후 localStorage 백업 확인 (복구 가능 여부 감지)
      await this.checkAndRestoreBackup();
    } catch (error) {
      console.error('[HomepageEditor] 초기 데이터 로드 실패:', error);
    } finally {
      this.isLoading = false;
    }

    // Ctrl+S / Cmd+S 단축키 등록
    window.addEventListener('keydown', this.handleKeyDown);
  },

  beforeUnmount() {
    // 컴포넌트 해제 시 단축키 이벤트 리스너 제거
    window.removeEventListener('keydown', this.handleKeyDown);
  },

  methods: {
    ...mapActions('homepageEditor', ['loadPageData', 'saveCurrentPage', 'checkAndRestoreBackup']),

    // 키보드 단축키 핸들러 (Ctrl+S / Cmd+S)
    handleKeyDown(event) {
      const isSaveShortcut = (event.ctrlKey || event.metaKey) && event.key === 's';
      if (isSaveShortcut) {
        event.preventDefault();
        if (this.isDirty) {
          this.saveCurrentPage().catch((err) => {
            console.error('[HomepageEditor] 단축키 저장 실패:', err);
          });
        }
      }
    },
  },
};
</script>
